// Instrument-style gauges for the station card: one geometry (a 270° arc,
// open at the bottom) for pressure, humidity, air quality and UV, a compass
// ring for wind, a tube for rain and a sky column for clouds. Colour is only
// used where it means something (AQI and UV scales, gusts, trends). Numbers
// are sized in container units so they always fit the dial they sit in.

import type { ReactNode } from "react";
import { Icon } from "../../ui/Icon";
import type { Band } from "./scales";

type IconName = Parameters<typeof Icon>[0]["name"];

const START = -135;
const SWEEP = 270;
const R = 40;

/** Point on the dial circle; 0° is straight up, clockwise. */
function polar(deg: number, r = R): [number, number] {
  const a = (deg * Math.PI) / 180;
  return [Math.sin(a) * r, -Math.cos(a) * r];
}

function arcPath(fromDeg: number, toDeg: number, r = R): string {
  const [x1, y1] = polar(fromDeg, r);
  const [x2, y2] = polar(toDeg, r);
  const large = toDeg - fromDeg > 180 ? 1 : 0;
  return `M${x1.toFixed(2)} ${y1.toFixed(2)} A${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
}

/** Fraction 0–1 along the arc → dial angle. */
const angleAt = (f: number) => START + SWEEP * Math.min(1, Math.max(0, f));

/** A tile: small-caps label, the instrument, then one or two short lines of context. */
export function GaugeTile({ icon, label, children, note, tone }: { icon: IconName; label: string; children: ReactNode; note?: ReactNode; tone?: string }) {
  return (
    <figure className="gt" style={tone ? { ["--tone" as string]: tone } : undefined}>
      <figcaption className="gt-label">
        <Icon name={icon} size="0.95em" /> {label}
      </figcaption>
      <div className="gt-vis">{children}</div>
      {note && <div className="gt-note">{note}</div>}
    </figure>
  );
}

/** The square dial area: SVG face plus centred HTML readout. */
function Dial({ face, children, label }: { face: ReactNode; children: ReactNode; label: string }) {
  return (
    <div className="g-dial" role="img" aria-label={label}>
      <svg viewBox="-50 -50 100 100" aria-hidden="true">
        {face}
      </svg>
      <div className="g-read">{children}</div>
    </div>
  );
}

/**
 * 270° arc gauge. `value` is a 0–1 fraction (null = no reading). Bands colour
 * the track (AQI, UV); without bands the arc fills up to the value in `fill`.
 * `ghost` marks an earlier value (the barometer's "set hand").
 */
export function ArcGauge({
  value,
  bands,
  fill,
  ghost,
  needle,
  ends,
  ticks = 0,
  label,
  children,
}: {
  value: number | null;
  bands?: Band[];
  fill?: string;
  ghost?: number | null;
  needle?: boolean;
  ends?: [string, string];
  ticks?: number;
  label: string;
  children: ReactNode;
}) {
  const v = value === null ? null : Math.min(1, Math.max(0, value));
  const face = (
    <>
      <path d={arcPath(START, START + SWEEP)} className="g-track" />
      {bands &&
        bands.map((b, i) => {
          const from = i === 0 ? 0 : bands[i - 1]!.to;
          // Small gaps between bands read as a scale, not a rainbow.
          const gap = i === 0 ? 0 : 1.6;
          return <path key={i} d={arcPath(angleAt(from) + gap, angleAt(b.to))} className="g-band" style={{ stroke: b.color }} />;
        })}
      {!bands && v !== null && v > 0.002 && <path d={arcPath(START, angleAt(v))} className="g-fill" style={fill ? { stroke: fill } : undefined} />}
      {ticks > 0 &&
        Array.from({ length: ticks + 1 }, (_, i) => {
          const a = angleAt(i / ticks);
          const [x1, y1] = polar(a, R - 7);
          const [x2, y2] = polar(a, R - (i % (ticks / 2) === 0 ? 11 : 9));
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} className="g-tick" />;
        })}
      {ghost != null && (
        <g transform={`rotate(${angleAt(Math.min(1, Math.max(0, ghost)))})`}>
          <path d={`M0 ${-R + 9} l-3.2 -5.5 h6.4 z`} className="g-ghost" />
        </g>
      )}
      {v !== null && needle && (
        <g transform={`rotate(${angleAt(v)})`}>
          {/* A pointer across the scale, not a full needle, so it never crosses the readout. */}
          <line y1={-R + 13} y2={-R - 4.5} className="g-needle" />
        </g>
      )}
      {v !== null && !needle && (bands || fill) && (
        <circle cx={polar(angleAt(v))[0]} cy={polar(angleAt(v))[1]} r="5.2" className="g-marker" />
      )}
      {ends && (
        <>
          <text x={polar(START, R)[0]} y={polar(START, R)[1] + 13} className="g-end">
            {ends[0]}
          </text>
          <text x={polar(START + SWEEP, R)[0]} y={polar(START + SWEEP, R)[1] + 13} className="g-end">
            {ends[1]}
          </text>
        </>
      )}
    </>
  );
  return (
    <Dial face={face} label={label}>
      {children}
    </Dial>
  );
}

/** Compass ring with the wind arrow: tail where it blows from, head downwind. */
export function WindCompass({ deg, calm, label, children }: { deg: number | null; calm: boolean; label: string; children: ReactNode }) {
  const face = (
    <>
      <circle r={R} className="g-track g-ring" />
      {Array.from({ length: 72 }, (_, i) => {
        const major = i % 18 === 0;
        const mid = i % 6 === 0;
        const [x1, y1] = polar(i * 5, R + 3.5);
        const [x2, y2] = polar(i * 5, major ? R - 4 : mid ? R - 2 : R - 0.5);
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} className={major ? "g-tick major" : mid ? "g-tick" : "g-tick minor"} />;
      })}
      {(["N", "E", "S", "W"] as const).map((c, i) => {
        const [x, y] = polar(i * 90, R - 9.5);
        return (
          <text key={c} x={x} y={y + 3.2} className="g-cardinal">
            {c}
          </text>
        );
      })}
      {!calm && deg !== null && (
        <g transform={`rotate(${deg})`}>
          <circle cy={-R} r="4" className="g-wind-tail" />
          <line y1={-R + 4} y2={R - 9} className="g-wind-shaft" />
          <path d={`M0 ${R + 1} L-6 ${R - 11} L0 ${R - 8} L6 ${R - 11} Z`} className="g-wind-head" />
        </g>
      )}
    </>
  );
  return (
    <Dial face={face} label={label}>
      <span className="g-read-bg">{children}</span>
    </Dial>
  );
}

/** A rain gauge tube filled to `fraction`, with the amount beside it. */
export function RainTube({ fraction, label, children }: { fraction: number; label: string; children: ReactNode }) {
  const f = Math.min(1, Math.max(0, fraction));
  return (
    <div className="g-rain" role="img" aria-label={label}>
      <div className="g-tube">
        <span style={{ height: `${f * 100}%` }} />
        {[0.25, 0.5, 0.75].map((t) => (
          <i key={t} style={{ bottom: `${t * 100}%` }} />
        ))}
      </div>
      <div className="g-rain-read">{children}</div>
    </div>
  );
}

export interface SkyLayer {
  cover: number;
  name: string;
  height: string;
}

/** Cloud layers as a column: highest at the top, bar width = how much sky it covers. */
export function SkyColumn({ layers, label, children }: { layers: SkyLayer[]; label: string; children: ReactNode }) {
  return (
    <div className="g-sky" role="img" aria-label={label}>
      <div className="g-sky-read">{children}</div>
      <ol className="g-sky-layers">
        {layers.length === 0 ? (
          <li className="is-clear">
            <span className="g-sky-bar" style={{ width: "0%" }} />
            <span className="g-sky-txt">No clouds below 12,000 ft</span>
          </li>
        ) : (
          [...layers].reverse().map((l, i) => (
            <li key={i}>
              <span className="g-sky-bar" style={{ width: `${Math.max(12, l.cover * 100)}%` }} />
              <span className="g-sky-txt">
                <b>{l.name}</b> {l.height}
              </span>
            </li>
          ))
        )}
      </ol>
    </div>
  );
}
